import React from 'react';
import { Loader2Icon } from 'lucide-react';
import { cn } from '../../utils/cn';
type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'success';
type Size = 'sm' | 'md';
interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  icon?: React.ReactNode;
  /** Shows a spinner, keeps the label, and blocks interaction. */
  loading?: boolean;
  /** Full-width button: opts out of the coarse-pointer min-width floor. */
  block?: boolean;
}

/**
 * Every variant declares its own text colour on EVERY state. Nothing here
 * inherits colour from a parent, a page, or the body token — a filled
 * button on a dark accent must never pick up dark body text.
 */
const VARIANTS: Record<Variant, string> = {
  primary: ['border border-brand-700 bg-brand-700 text-white', 'hover:border-brand-800 hover:bg-brand-800 hover:text-white', 'focus-visible:bg-brand-800 focus-visible:text-white', 'active:border-brand-900 active:bg-brand-900 active:text-white'].join(' '),
  secondary: ['border border-line-strong bg-surface text-ink', 'hover:border-ink-subtle hover:bg-canvas hover:text-ink', 'focus-visible:text-ink active:bg-canvas active:text-ink'].join(' '),
  ghost: ['border border-transparent bg-transparent text-ink-muted', 'hover:bg-canvas hover:text-ink focus-visible:text-ink active:bg-canvas active:text-ink'].join(' '),
  danger: ['border border-danger-700 bg-danger-700 text-white', 'hover:border-danger-800 hover:bg-danger-800 hover:text-white', 'focus-visible:text-white active:bg-danger-800 active:text-white'].join(' '),
  success: ['border border-ok-700 bg-ok-700 text-white', 'hover:border-ok-800 hover:bg-ok-800 hover:text-white', 'focus-visible:text-white active:bg-ok-800 active:text-white'].join(' ')
};

/**
 * Heights meet the 44px touch minimum; sizes meet the 16px text minimum.
 * `sm` is a denser button, never a smaller-text one.
 */
const SIZES: Record<Size, string> = {
  sm: 'min-h-touch px-3.5 py-2 text-body-sm gap-1.5 rounded-md',
  md: 'min-h-touch-lg px-4 py-2.5 text-body gap-2 rounded-lg'
};

/** An intentional disabled pair, not a faded version of the variant. */
const DISABLED = 'disabled:cursor-not-allowed disabled:border-line disabled:bg-canvas disabled:text-ink-muted disabled:shadow-none';
export function Button({
  variant = 'secondary',
  size = 'md',
  icon,
  loading = false,
  block = false,
  className,
  children,
  disabled,
  ...props
}: ButtonProps) {
  return <button className={cn('inline-flex items-center justify-center font-medium transition-colors duration-150 ease-out', SIZES[size], VARIANTS[variant], DISABLED, block && 'cz-block w-full', className)} disabled={disabled || loading} aria-busy={loading || undefined} {...props} data-id="element-5178" data-mp="2c7d48z8vtnpk">
      {loading ? <Loader2Icon aria-hidden className="h-4 w-4 shrink-0 animate-spin" data-id="element-5179" data-mp="rpp1tk09hsnx" /> : icon}
      {children}
    </button>;
}